import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, InMemoryCache } from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import * as Sentry from "@sentry/browser";
import { excludeGraphQLFetch, SentryLink } from "apollo-link-sentry";
import Amplify, { Auth } from "aws-amplify";
import mixpanel from "mixpanel-browser";
import React, { useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { FlagsProvider, useFlag } from "react-unleash-flags";

import useRefOf from "../../shared/hooks/useRefOf";
import AppRouter from "./App";
import ErrorReporting from "./ErrorReporting";
import logOut from "./components/auth/logOut";
import "./styles/styles.scss";

const flagConfig = {
  appName: import.meta.env.REACT_APP_GITLAB_ENVIRONMENT ?? "development",
  host: "https://gitlab.com/api/v4/feature_flags/unleash/12602311",
  instanceId: "tz5ma-BHLsf1Yu5-r7QY",
};

export default function main() {
  Amplify.configure({
    aws_cognito_region: import.meta.env.REACT_APP_COGNITO_REGION,
    aws_project_region: import.meta.env.REACT_APP_COGNITO_REGION,
    aws_user_pools_id: import.meta.env.REACT_APP_COGNITO_USER_POOL_ID,
    aws_user_pools_web_client_id: import.meta.env.REACT_APP_COGNITO_CLIENT_ID,
  });
  Auth.configure({
    authenticationFlowType: import.meta.env.REACT_APP_COGNITO_LOCAL ? "USER_PASSWORD_AUTH" : "USER_SRP_AUTH",
    endpoint: import.meta.env.REACT_APP_COGNITO_LOCAL,
    oauth: {
      domain: import.meta.env.REACT_APP_COGNITO_DOMAIN,
      redirectSignIn: import.meta.env.REACT_APP_DOMAIN,
      redirectSignOut: import.meta.env.REACT_APP_DOMAIN,
      responseType: "code",
    },
  });

  if (import.meta.env.REACT_APP_SENTRY_ENVIRONMENT) {
    Sentry.init({
      dsn: "https://fc5e2b9260464613a18f56f2105b897d@o392151.ingest.sentry.io/5239314",
      beforeBreadcrumb: excludeGraphQLFetch,
      release: import.meta.env.REACT_APP_SENTRY_RELEASE as string,
      environment: import.meta.env.REACT_APP_SENTRY_ENVIRONMENT as string,
    });
  }

  mixpanel.init(import.meta.env.REACT_APP_MIXPANEL_TOKEN as string, {
    opt_out_tracking_by_default: true,
    secure_cookie: true,
    api_host: "https://api-eu.mixpanel.com",
  });
  if (import.meta.env.REACT_APP_SENTRY_ENVIRONMENT !== "prod") {
    mixpanel.set_config({ debug: true });
  }

  createRoot(document.getElementById("root")!).render(
    <ErrorReporting>
      <FlagsProvider config={flagConfig}>
        <AppWithApollo />
      </FlagsProvider>
    </ErrorReporting>,
  );
}

function AppWithApollo() {
  const batchFlagRef = useRefOf(useFlag("api-batching"));
  const [showNewVersionToast, setShowNewVersionToast] = useState(false);
  const apolloClient = useMemo(
    () =>
      new ApolloClient({
        link: ApolloLink.from([
          new SentryLink({
            uri: `${import.meta.env.REACT_APP_API_URL}/graphql`,
            setTransaction: false,
            attachBreadcrumbs: {
              includeQuery: true,
              includeVariables: false,
              includeError: true,
              includeCache: false,
            },
          }),
          onError(({ graphQLErrors, networkError }) => {
            if (
              networkError &&
              typeof networkError === "object" &&
              "statusCode" in networkError &&
              networkError.statusCode >= 500
            ) {
              Sentry.captureException(networkError);
            }
            for (const graphQLError of graphQLErrors ?? []) {
              if (
                graphQLError.extensions?.code === "FORBIDDEN" ||
                graphQLError.extensions?.code === "UNAUTHORIZED" ||
                graphQLError.message === "Your session has expired"
              ) {
                continue;
              }
              // this seems to be needed to ensure the breadcrumbs get correctly set
              setImmediate(() =>
                Sentry.captureException(new Error(graphQLError.message), {
                  fingerprint: ["{{ default }}", graphQLError.message],
                  extra: graphQLError.extensions
                    ? { code: graphQLError.extensions.code, ...(graphQLError.extensions.exception ?? {}) }
                    : {},
                }),
              );
            }
          }),
          new ApolloLink((operation, forward) =>
            forward(operation).map((response) => {
              const {
                response: { headers },
              } = operation.getContext();
              const serverVersion = headers?.get("CultureShift-Version");
              if (
                import.meta.env.REACT_APP_SENTRY_RELEASE &&
                serverVersion !== import.meta.env.REACT_APP_SENTRY_RELEASE
              ) {
                setShowNewVersionToast(true);
              }
              return response;
            }),
          ),
          setContext(async (_, context) => {
            try {
              const session = await Auth.currentSession();
              if (session.isValid()) {
                return {
                  ...context,
                  headers: {
                    ...context.headers,
                    authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
                  },
                  doNotBatch: context.doNotBatch || !batchFlagRef.current?.enabled,
                };
              }
            } catch (e) {
              // We expect a "No current user" error if the user's token is expired.
              if (e instanceof Error && e?.message !== "No current user") throw e;
            }

            // If for any reason we couldn't authenticate the user's credentials,
            // clear them and return to the login screen
            await logOut();
            throw new Error("Your session has expired");
          }),
          ApolloLink.split(
            (operation) => operation.getContext().doNotBatch,
            createHttpLink({ uri: `${import.meta.env.REACT_APP_API_URL}/graphql` }),
            new BatchHttpLink({ uri: `${import.meta.env.REACT_APP_API_URL}/graphql`, batchMax: 25, batchInterval: 5 }),
          ),
        ]),
        cache: new InMemoryCache({
          possibleTypes: {
            CustomReportAccessFilter: ["CustomReportAccessFilterByQuestion", "CustomReportAccessFilterByForm"],
          },
        }),
        defaultOptions: { watchQuery: { fetchPolicy: "cache-and-network" } },
      }),
    [batchFlagRef],
  );

  return (
    <ApolloProvider client={apolloClient}>
      <AppRouter showNewVersionToast={showNewVersionToast} />
    </ApolloProvider>
  );
}
